import { ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { CropperModal } from '@components/CropperModal'
import Image from '@components/Image'
import ImageStream, { ImageStreamLoaderFN } from '@components/ImageStream/ImageStream'

import AxiosUtil from '@/utils/axiosUtil'
import BlobUtil from '@/utils/blobUtil'
import StringUtil from '@/utils/stringUtil'
import TypeUtil from '@/utils/typeUtil'

export type ImageUploadProps<T> = {
  initialImageSrc?: string | ImageStreamLoaderFN
  accept?: Array<string>
  width?: number
  height?: number
  aspectRatio?: number
  modalTitle?: ReactNode
  isDisabled?: boolean
  uploadFn: (
    file: File,
    onUploadProgress?: (ProgressEvent: ProgressEvent) => void,
    controller?: AbortController
  ) => Promise<T>
  onUploaded?: (file: T) => void
  onError?: () => void
}

const ImageUpload = <T,>({
  initialImageSrc,
  aspectRatio,
  accept,
  height,
  width,
  modalTitle,
  isDisabled,
  uploadFn,
  onUploaded,
  onError,
}: ImageUploadProps<T>) => {
  const id = useId()
  const intl = useIntl()
  const progressBarRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [uploadedImage, setUploadedImage] = useState<string>()
  const [filename, setFilename] = useState<string>()
  const [filetype, setFiletype] = useState<string>()
  const [error, setError] = useState<string>()
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [isShowCropper, setIsShowCropper] = useState<boolean>(false)

  const preview: string | ImageStreamLoaderFN | undefined = uploadedImage || initialImageSrc

  const handleInputClick = async () => {
    inputRef.current!.value = ''
  }

  const resetState = () => {
    setUploadedImage(undefined)
    setFilename(undefined)
    setFiletype(undefined)
    setError(undefined)
    setIsUploading(false)
    setIsShowCropper(false)
  }

  const updateProgressBar = (percentage: number) => {
    progressBarRef.current?.style.setProperty('width', `${percentage}%`)
  }

  const handleCrop = async (blob: Blob) => {
    try {
      setIsUploading(true)
      const name = filename ?? `${StringUtil.generateUUID4()}.png`
      const file = new File([blob], name, { type: filetype })
      setUploadedImage(await BlobUtil.toDataUrl(blob))

      const response = await uploadFn(file, ({ loaded, total }) => updateProgressBar((loaded / total) * 100))
      onUploaded?.(response)
    } catch (err) {
      resetState()
      onError?.()
      if (AxiosUtil.isAxiosError(err)) {
        setError(err.response?.data.message)
      } else {
        setError(String(err))
      }
    } finally {
      setIsUploading(false)
      updateProgressBar(0)
      setIsShowCropper(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      const file = e.currentTarget.files[0]
      setUploadedImage(URL.createObjectURL(file))
      setFilename(file.name)
      setFiletype(file.type)
      setError(undefined)
      setIsShowCropper(true)
    }
  }

  useEffect(() => {
    return () => {
      if (uploadedImage) {
        URL.revokeObjectURL(uploadedImage)
      }
    }
  }, [uploadedImage])

  return (
    <div>
      <input
        hidden
        ref={inputRef}
        name={id}
        type="file"
        value={undefined}
        accept={accept?.join(',')}
        onClick={handleInputClick}
        onChange={handleInputChange}
      />

      <div className="d-flex">
        <div
          className="w-100px h-100px rounded bg-gray-100 border-secondary d-flex align-items-center justify-content-center"
          onClick={() => !isDisabled && inputRef.current!.click()}
          style={{
            border: !preview ? '2px dashed' : '1px solid',
            cursor: isDisabled ? 'default' : 'pointer',
          }}
        >
          {preview ? (
            TypeUtil.isFunction(preview) ? (
              <ImageStream
                loader={preview}
                height="100%"
                width="100%"
                className="object-contain rounded"
              />
            ) : (
              <Image
                src={preview}
                height="100%"
                width="100%"
                className="object-contain rounded"
              />
            )
          ) : (
            <i className="fa-solid fa-image fs-2qx" />
          )}
        </div>

        <div className="ms-5 d-flex align-items-center">
          {filename ? (
            <div className="d-flex flex-column gap-2">
              <span className="fs-6">{filename}</span>

              {!error && isUploading ? (
                <div className="progress">
                  <div
                    ref={progressBarRef}
                    className="progress-bar bg-primary"
                    role="progressbar"
                  />
                </div>
              ) : (
                <span className="fs-4 fw-medium text-success">{intl.formatMessage({ id: 'file.upload.success' })}</span>
              )}
            </div>
          ) : (
            <div className="d-flex flex-column">
              <span className="fs-6">JPG, JPEG, JFIF, PNG</span>
              {error && <div className="alert alert-danger d-flex align-items-center p-1">{error}</div>}
            </div>
          )}
        </div>
      </div>

      <CropperModal
        isShow={isShowCropper}
        onCancel={resetState}
        onCrop={handleCrop}
        aspectRatio={aspectRatio}
        imageSrc={uploadedImage}
        imageWidth={width}
        imageHeight={height}
        modalTitle={modalTitle}
      />
    </div>
  )
}

export { ImageUpload }
